import styled from 'styled-components';
import { chatKeyboardFocusVisible } from '../../styles/chatFocus';

interface StyledPanelProps {
  isOpen: boolean;
}

export const StyledPanel = styled.div<StyledPanelProps>`
  /* Slide in animation for desktop */
  @keyframes slideInPanelDesktop {
    from {
      transform: translateX(100%);
    }
    to {
      transform: translateX(0%);
    }
  }

  /* Slide out animation for desktop */
  @keyframes slideOutPanelDesktop {
    from {
      transform: translateX(0%);
    }
    to {
      transform: translateX(100%);
    }
  }

  /* Slide in animation for mobile/tablet */
  @keyframes slideInPanelMobile {
    from {
      transform: translateY(100%);
    }
    to {
      transform: translateY(0%);
    }
  }

  /* Slide out animation for mobile/tablet */
  @keyframes slideOutPanelMobile {
    from {
      transform: translateY(0%);
    }
    to {
      transform: translateY(100%);
    }
  }

  position: fixed;
  top: 87px;
  border-radius: 14px 0 0 14px;
  right: 0;
  width: 50%;
  height: calc(100svh - 87px);
  background: white;
  box-shadow: 0 7px 20px 0 rgba(0, 0, 0, 0.25);
  z-index: 1000;
  transform: translateX(${props => (props.isOpen ? '0' : '100%')});
  overflow: hidden;
  will-change: transform;
  backface-visibility: hidden;
  animation: ${props => (props.isOpen ? 'slideInPanelDesktop' : 'slideOutPanelDesktop')} 0.8s
    cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
  pointer-events: ${props => (props.isOpen ? 'auto' : 'none')};

  @media (max-width: 992px) {
    width: 100%;
    z-index: 1001;
    top: 0;
    height: 100vh;
    height: 100svh; /* Accounts for dynamic address bar in iOS */
    border-radius: 14px 14px 0 0;
    transform: translateY(${props => (props.isOpen ? '0' : '100%')});
    animation: ${props => (props.isOpen ? 'slideInPanelMobile' : 'slideOutPanelMobile')} 0.8s
      cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
  }

  @media (min-width: 1200px) {
    width: 47.9vw;
  }
  .hqv-modal .menu-section-rates {
    border-top: 1px solid var(--Colors-neutral-30, #c4c4c4);
    @media (max-width: 992px) {
      height: 72px;
    }
  }

  /* Hide only the price display for single property response (availability_calendar_search)
     Keep the View Rates CTA button visible */
  &.hide-rates {
    .menu-section-rates {
      /* Hide price container and price sub-section */
      .price-sub-section {
        display: none !important;
      }
      /* Hide rate unavailable text but keep the CTA */
      .rate-unavailable-text,
      .soldout-text,
      .opening-soon-text,
      .flexi-opening-soon {
        display: none !important;
      }
    }
  }
`;

export const PanelContent = styled.div`
  height: calc(100% - 131px);
  overflow-y: auto;
  background: white;

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    display: none;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }

  .gap-2 {
    gap: 0.5rem;
  }

  .hqv-modal-content {
    padding-bottom: 2rem;

    /* Apply HQV modal styles to nested components */
    .hotel-overview-container,
    .information-component,
    .amenities-component,
    .property-details-component {
      &.hqv-modal {
        /* HQV modal styles are automatically applied by the components */
      }
    }
  }
`;

export const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  background: white;
  position: sticky;
  top: 0;
  z-index: 10;

  h2 {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 600;
    color: #1f2937;
    flex: 1;
    padding-right: 1rem;
    line-height: 1.3;

    @media (max-width: 992px) {
      font-size: 1.25rem;
    }
  }
`;

export const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  @media (max-width: 992px) {
    flex-direction: row-reverse;
  }
`;

export const FavoriteToggleButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: none;
  border-radius: 4px;
  cursor: pointer;
  color: black;
  flex-shrink: 0;

  ${chatKeyboardFocusVisible}
`;

export const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: #eeeef0;
  border-radius: 4px;
  cursor: pointer;
  color: black;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background: #e5e7eb;
    color: #374151;
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }

  svg {
    width: 20px;
    height: 20px;
  }

  ${chatKeyboardFocusVisible}
`;

export const PanelOverlay = styled.div<StyledPanelProps>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  height: 100svh; /* Accounts for dynamic address bar in iOS */
  background: rgba(0, 0, 0, 0.3);
  z-index: 999;
  opacity: ${props => (props.isOpen ? 1 : 0)};
  visibility: ${props => (props.isOpen ? 'visible' : 'hidden')};
  transition: opacity 0.3s ease, visibility 0.3s ease;

  @media (min-width: 992px) {
    display: none; /* No overlay on desktop, panel slides over content */
  }
`;
